import { Button, IconPlusCircle, Wrapper } from '@screentone/core';
import { v4 as uuidv4 } from 'uuid';

import { ReadOnlyTooltip } from 'components/context-menu-actions/shared/ReadOnlyTooltip';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { GraphicLinkedItem, LinkedItem, MobileLinkedItem, ModuleContainer } from 'data/generated/graphql';
import { GRAPHIC_LINKED_ITEM, MOBILE_LINKED_ITEM } from 'utils/constants';

import {
  DEFAULT_GRAPHIC_LINKED_ITEM_ATTRIBUTES,
  DEFAULT_MOBILE_LINKED_ITEM_ATTRIBUTES
} from 'components/datamodel/content/LinkedItem/LinkedItem.constants';

type Variant = 'regular' | 'mobile' | 'graphics';

interface BaseProps {
  hierarchyId: string;
  module: ModuleContainer;
  viewOnly?: boolean;
}

const makeBaseLinkedItem = (): LinkedItem => ({
  type: 'LinkedItem',
  uuid: uuidv4(),
  attributes: {
    type: 'link',
    id: '',
    hosted_url: '',
    product: '',
    title: '',
    description: '',
    image: {
      id: '',
      alternate_text: '',
      credit: '',
      height: 0,
      name: '',
      reuse_type: '',
      media_type: '',
      width: 0,
      src: {
        params: { size: 0 },
        image_id: '',
        base_url: '',
        path: ''
      },
      alt_images: [],
      properties: { imphotoid: '', location: '' },
      type: 'image'
    }
  }
});

const makeLinkedItem = (variant: Variant): LinkedItem | MobileLinkedItem | GraphicLinkedItem => {
  const base = makeBaseLinkedItem();

  if (variant === 'mobile') {
    return {
      ...base,
      subtype: MOBILE_LINKED_ITEM,
      attributes: {
        ...base.attributes,
        ...DEFAULT_MOBILE_LINKED_ITEM_ATTRIBUTES
      }
    } as MobileLinkedItem;
  }

  if (variant === 'graphics') {
    return {
      ...base,
      subtype: GRAPHIC_LINKED_ITEM,
      attributes: { ...base.attributes, ...DEFAULT_GRAPHIC_LINKED_ITEM_ATTRIBUTES }
    } as GraphicLinkedItem;
  }

  return base;
};

interface AddLinkedItemButtonProps extends BaseProps {
  variant?: Variant; // 'regular' (default) | 'mobile'
  label?: string; // optional override button text
  postAction?: () => void; // optional callback after adding the linked item
}

export const AddLinkedItemButton = ({
  hierarchyId,
  module,
  variant = 'regular',
  label = 'Add linked item',
  postAction = () => {},
  viewOnly = false
}: AddLinkedItemButtonProps) => {
  const { insertEntity, isEditingLinkedItem, setIsEditingLinkedItem } = useDataModelContext();

  const handleClick = () => {
    const item = makeLinkedItem(variant);
    const newItemIndex = module.collection.length;
    insertEntity(`${hierarchyId}-${newItemIndex}`, item);
    setIsEditingLinkedItem((prev) => new Set(prev).add(item.uuid));
    postAction();
  };

  const hasOpenLinkedItemInModule = module.collection.some(
    (item) => item.type === 'LinkedItem' && isEditingLinkedItem.has(item.uuid)
  );

  return (
    <Wrapper>
      <ReadOnlyTooltip disabled={viewOnly}>
        <Button disabled={hasOpenLinkedItemInModule || viewOnly} icon={IconPlusCircle} onClick={handleClick} tertiary>
          {label}
        </Button>
      </ReadOnlyTooltip>
    </Wrapper>
  );
};

export const AddLinkedItem = (props: AddLinkedItemButtonProps) => <AddLinkedItemButton {...props} variant="regular" />;

export const AddMobileLinkedItem = (props: BaseProps) => (
  <AddLinkedItemButton {...props} variant="mobile" />
);
